// src/components/sections/ClientMetrics.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Divider, IconButton, Tooltip } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import CardSectionLabel from '../cards/CardSectionLabel';

const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : Number(value);
  if (isNaN(num)) return '$0';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${Math.round(num / 1_000)}K`;
  return `$${Math.round(num).toLocaleString('en-US')}`;
};

const Stat = ({ label, value }) => (
  <Box sx={{ flex: 1, textAlign: 'center', px: 0.5 }}>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', mt: 0.25, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </Typography>
  </Box>
);

const ClientMetrics = ({ clientId, metricsData, onMetricsClick }) => {
  const {
    total_revenue = 0,
    active_projects = 0,
    total_projects = 0,
    avg_project_value = 0,
    open_opportunities = 0
  } = metricsData || {};

  return (
    <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25 }}>
      <CardSectionLabel
        label="Engagement"
        action={
          <Tooltip title="View detailed metrics">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onMetricsClick && onMetricsClick(e); }}
              sx={{ color: 'text.disabled', p: 0.25, '&:hover': { color: 'primary.main' } }}
            >
              <BarChartIcon sx={{ fontSize: '0.85rem' }} />
            </IconButton>
          </Tooltip>
        }
      />

      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Stat label="Lifetime Revenue" value={formatCurrency(total_revenue)} />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Stat label="Active / Total" value={`${active_projects} / ${total_projects}`} />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Stat label="Avg Project" value={formatCurrency(avg_project_value)} />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Stat label="Open Opps" value={open_opportunities} />
      </Box>
    </Box>
  );
};

ClientMetrics.propTypes = {
  clientId: PropTypes.string,
  metricsData: PropTypes.shape({
    total_revenue: PropTypes.number,
    active_projects: PropTypes.number,
    total_projects: PropTypes.number,
    avg_project_value: PropTypes.number,
    open_opportunities: PropTypes.number,
    client_satisfaction: PropTypes.number
  }),
  onMetricsClick: PropTypes.func
};

export default ClientMetrics;
